import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import dayjs from 'dayjs';

import { IClient, IConsultation } from '../../../store/types';
import { getMonday, addDateBy, lessDateBy, weekDates, getDate, getTime } from '../../../utils/getTime';
import { ArrowLeft } from '../../icons/arrow-left';
import Button from '../../common/button';
import { ConsultationHoverPopup } from '../consultation-hover-popup';

export interface ScheduleProps {
  close: () => void;
  openCreateForm: () => void;
  clients: Pick<IClient, 'id' | 'full_name' | 'time_zone'>[];
  consultations: IConsultation[];
  handleChangeCons: (
    id: number,
    options?: { isEdit?: boolean; overrideStart?: string; overrideEnd?: string; overrideDate?: string },
  ) => void;
  onRescheduleRequest: (payload: {
    id: number;
    newStart: string;
    newEnd: string;
    originalStart: string;
    originalEnd: string;
  }) => void;
  onCreateFromSlot: (payload: { date: string; start: string; end: string }) => void;
  handleOpenDelete?: (item: IConsultation, isCalendar: boolean) => void;
}

const HEIGHT_HOUR = 54;

/** Ширина сетки календаря: `grid-cols-[66px_ + 7×104px]` — шапка по ширине совпадает с сеткой */
const CALENDAR_GRID_WIDTH_PX = 66 + 7 * 104;

const getCurrentTimeOffset = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  return hours * HEIGHT_HOUR + (minutes * HEIGHT_HOUR) / 60;
};

export const Schedule: FC<ScheduleProps> = ({
  close,
  openCreateForm,
  clients,
  consultations,
  handleChangeCons,
  onRescheduleRequest,
  onCreateFromSlot,
  handleOpenDelete,
}) => {
  const [monday, setMonday] = useState(getMonday(new Date()));
  const [consultationList, setConsultationList] = useState<IConsultation[]>([]);
  const [copy, setCopy] = useState(false);
  const [dragInfo, setDragInfo] = useState<{
    consultation: IConsultation;
    pointerOffset?: number;
  } | null>(null);
  const [hoveredConsultation, setHoveredConsultation] = useState<{
    consultation: IConsultation;
    client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  } | null>(null);
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentTimeOffset, setCurrentTimeOffset] = useState(getCurrentTimeOffset());
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const currentWeekMonday = getMonday(new Date());
  const isCurrentWeek = dayjs(monday).isSame(dayjs(currentWeekMonday), 'day');

  const scrollToRef = useRef<HTMLDivElement | null>(null);
  const scrollToCardNum = 19;

  useEffect(() => {
    // If `scrollToRef` points to an element, then scroll it into view.
    if (scrollToRef.current) {
      scrollToRef?.current?.scrollIntoView({
        block: 'nearest',
        inline: 'start',
      });
    }
  }, []);

  useEffect(() => {
    if (!copy) return;

    const timer = setTimeout(() => {
      setCopy(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [copy]);

  useEffect(() => {
    if (consultations) {
      setConsultationList(consultations);
    }
  }, [consultations]);

  useEffect(() => {
    const container = calendarRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Intentionally left blank; kept in case future scroll reactions are needed.
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeOffset(getCurrentTimeOffset());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const getNextWeek = () => {
    const newMonday = addDateBy(monday, 7);
    setMonday(newMonday);
  };
  const getLastWeek = () => {
    const newMonday = lessDateBy(monday, 7);
    setMonday(newMonday);
  };
  const resetToCurrentWeek = () => {
    setMonday(getMonday(new Date()));
  };

  return (
    <>
      <div className="w-full h-max flex flex-col overflow-x-auto">
        <div
          className="mb-[28px] flex w-full flex-col gap-[20px] sm:flex-row sm:items-center sm:justify-between sm:gap-[28px]"
          style={{ maxWidth: CALENDAR_GRID_WIDTH_PX }}
        >
          <div className="flex min-w-0 shrink items-center gap-5">
            <button onClick={() => close()} className="group">
              <ArrowLeft className="fill-blackBase group-hover:fill-redBase" />
            </button>
            <h1 className="font-comfortaa font-bold text-2xl">Календарь встреч</h1>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
            {!isCurrentWeek && (
              <Button
                type="grey"
                className="flex h-10 w-full items-center gap-5 !w-max !p-[3px_3px] !text-base"
                onClick={resetToCurrentWeek}
              >
                Сегодня
              </Button>
            )}
            <Button
              type={'primary'}
              onClick={() => openCreateForm()}
              className="flex h-10 w-full items-center gap-5 !w-max !p-[10px_30px] !text-base"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 4.10742V15.8925M4.10744 9.99998H15.8926"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Создать встречу
            </Button>
          </div>
        </div>
        <div className="w-full flex flex-col gap-[30px]">
          <div className="w-fit min-w-[100%] flex items-center gap-[25px]">
            <Button
              type="grey"
              className="!p-[10px] max-w-[40px] h-[40px] flex items-center group"
              onClick={() => getLastWeek()}
            >
              <svg
                className="fill-blackBase group-hover:fill-white"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.88981 10.4417C2.77277 10.3245 2.70703 10.1657 2.70703 10C2.70703 9.83441 2.77277 9.67556 2.88981 9.55837L7.88981 4.55837C7.94703 4.49696 8.01603 4.44771 8.0927 4.41355C8.16936 4.37939 8.25213 4.36102 8.33605 4.35954C8.41996 4.35806 8.50332 4.3735 8.58115 4.40493C8.65897 4.43637 8.72966 4.48315 8.78901 4.5425C8.84836 4.60185 8.89515 4.67255 8.92658 4.75037C8.95802 4.82819 8.97345 4.91155 8.97197 4.99547C8.97049 5.07939 8.95213 5.16215 8.91796 5.23882C8.8838 5.31548 8.83455 5.38448 8.77315 5.4417L4.83981 9.37503L16.6648 9.37503C16.8306 9.37503 16.9895 9.44088 17.1068 9.55809C17.224 9.6753 17.2898 9.83427 17.2898 10C17.2898 10.1658 17.224 10.3248 17.1068 10.442C16.9895 10.5592 16.8306 10.625 16.6648 10.625L4.83981 10.625L8.77315 14.5584C8.83455 14.6156 8.88381 14.6846 8.91797 14.7613C8.95213 14.8379 8.97049 14.9207 8.97197 15.0046C8.97345 15.0885 8.95802 15.1719 8.92658 15.2497C8.89515 15.3275 8.84836 15.3982 8.78901 15.4576C8.72966 15.5169 8.65897 15.5637 8.58115 15.5951C8.50332 15.6266 8.41997 15.642 8.33605 15.6405C8.25213 15.639 8.16937 15.6207 8.0927 15.5865C8.01603 15.5524 7.94703 15.5031 7.88981 15.4417L2.88981 10.4417Z"
                />
              </svg>
            </Button>
            <div className="w-full flex max-w-[728px]">
              <div
                className={classNames(
                  'w-full max-w-[104px] p-[5px_35px] flex flex-col items-center gap-[1px] rounded-[15px_0px_0px_15px] border border-[#C7C7C799]',
                  {
                    ['!border-[#C00000]']: new Date().toDateString() === monday.toDateString(),
                  },
                )}
                style={{ height: HEIGHT_HOUR }}
              >
                <p className="font-medium text-base">{dayjs(monday).format('DD.MM')}</p>
                <p className="text-sm text-[#585858]">{dayjs(monday).format('dd')}</p>
              </div>
              {weekDates(monday).map((el, idx) => (
                <div
                  key={idx}
                  className={classNames(
                    'w-full max-w-[104px] p-[5px_35px] flex flex-col items-center gap-[1px] last:rounded-[0px_15px_15px_0px] border border-[#C7C7C799]',
                    {
                      ['!border-[#C00000]']: new Date().toDateString() === new Date(el).toDateString(),
                    },
                  )}
                  style={{ maxHeight: HEIGHT_HOUR }}
                >
                  <p
                    className={classNames('font-medium text-base', {
                      'text-redBase': new Date().toDateString() === new Date(el).toDateString(),
                    })}
                  >
                    {dayjs(new Date(el)).format('DD.MM')}
                  </p>
                  <p className="text-sm text-[#585858]">{dayjs(new Date(el)).format('dd')}</p>
                </div>
              ))}
            </div>
            <Button
              type="grey"
              className="!p-[10px] max-w-[40px] h-[40px] rotate-180 flex items-center group mr-5 md:mr-0"
              onClick={() => getNextWeek()}
            >
              <svg
                className="fill-blackBase group-hover:fill-white"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.88981 10.4417C2.77277 10.3245 2.70703 10.1657 2.70703 10C2.70703 9.83441 2.77277 9.67556 2.88981 9.55837L7.88981 4.55837C7.94703 4.49696 8.01603 4.44771 8.0927 4.41355C8.16936 4.37939 8.25213 4.36102 8.33605 4.35954C8.41996 4.35806 8.50332 4.3735 8.58115 4.40493C8.65897 4.43637 8.72966 4.48315 8.78901 4.5425C8.84836 4.60185 8.89515 4.67255 8.92658 4.75037C8.95802 4.82819 8.97345 4.91155 8.97197 4.99547C8.97049 5.07939 8.95213 5.16215 8.91796 5.23882C8.8838 5.31548 8.83455 5.38448 8.77315 5.4417L4.83981 9.37503L16.6648 9.37503C16.8306 9.37503 16.9895 9.44088 17.1068 9.55809C17.224 9.6753 17.2898 9.83427 17.2898 10C17.2898 10.1658 17.224 10.3248 17.1068 10.442C16.9895 10.5592 16.8306 10.625 16.6648 10.625L4.83981 10.625L8.77315 14.5584C8.83455 14.6156 8.88381 14.6846 8.91797 14.7613C8.95213 14.8379 8.97049 14.9207 8.97197 15.0046C8.97345 15.0885 8.95802 15.1719 8.92658 15.2497C8.89515 15.3275 8.84836 15.3982 8.78901 15.4576C8.72966 15.5169 8.65897 15.5637 8.58115 15.5951C8.50332 15.6266 8.41997 15.642 8.33605 15.6405C8.25213 15.639 8.16937 15.6207 8.0927 15.5865C8.01603 15.5524 7.94703 15.5031 7.88981 15.4417L2.88981 10.4417Z"
                />
              </svg>
            </Button>
          </div>
          <div
            ref={calendarRef}
            className="relative w-fit grid grid-cols-[66px_104px_104px_104px_104px_104px_104px_104px] overflow-y-scroll h-[calc(100vh_-_300px)] no-scrollbar border-[#C7C7C799]"
          >
            <div
              className="absolute left-0 w-full border-t border-dashed z-10 pointer-events-none"
              style={{ top: `${currentTimeOffset}px`, borderColor: '#FCA5A5' }}
            ></div>
            <div className="grid h-fit grid-rows-24">
              {Array.from({ length: 24 }, (_, i) => (
                <div
                  key={i}
                  ref={i === scrollToCardNum ? scrollToRef : null}
                  className="leading-none"
                  style={{ height: HEIGHT_HOUR }}
                >
                  <span>{`${i < 10 ? `0${i}` : i}:00`}</span>
                </div>
              ))}
            </div>
            {dragInfo &&
              Array.from({ length: 24 }, (_, hour) => (
                <div
                  key={`grid-line-${hour}`}
                  className="absolute border-t border-dashed border-[#E4E4E4] pointer-events-none"
                  style={{ top: `${hour * HEIGHT_HOUR}px`, left: 66, right: 0 }}
                />
              ))}
            {[monday].concat(weekDates(monday)).map((el, idx) => (
              <div
                className="w-full max-w-[104px] p-[5px_35px] flex flex-col relative items-center gap-[5px] border border-[#C7C7C799] break-all"
                key={idx}
                ref={(node) => {
                  dayRefs.current[idx] = node;
                }}
                onDragOver={(event) => {
                  if (dragInfo) {
                    event.preventDefault();
                  }
                }}
                onDrop={(event) => {
                  if (!dragInfo) {
                    return;
                  }

                  event.preventDefault();

                  const columnElement = dayRefs.current[idx];
                  if (!columnElement) return;

                  const rect = columnElement.getBoundingClientRect();
                  const relativeY = event.clientY - rect.top;
                  const pointerOffset = dragInfo.pointerOffset ?? 0;
                  const adjustedY = relativeY - pointerOffset;
                  const minutesPerPixel = 60 / HEIGHT_HOUR;
                  const tentativeMinutes = Math.round((adjustedY * minutesPerPixel) / 15) * 15;

                  const originalStart = dayjs(dragInfo.consultation.datetime_start);
                  const originalEnd = dayjs(dragInfo.consultation.datetime_end);
                  const durationMinutes = Math.max(originalEnd.diff(originalStart, 'minute'), 15);

                  const dayDate = dayjs(new Date(el));
                  let clampedMinutes = Math.max(0, tentativeMinutes);
                  const minutesInDay = 24 * 60;
                  const maxStart = minutesInDay - durationMinutes;
                  if (clampedMinutes > maxStart) {
                    clampedMinutes = maxStart;
                  }

                  const newStart = dayDate.startOf('day').add(clampedMinutes, 'minute');
                  const newEnd = newStart.add(durationMinutes, 'minute');

                  onRescheduleRequest({
                    id: dragInfo.consultation.id,
                    newStart: newStart.toISOString(),
                    newEnd: newEnd.toISOString(),
                    originalStart: dragInfo.consultation.datetime_start,
                    originalEnd: dragInfo.consultation.datetime_end,
                  });

                  setDragInfo(null);
                }}
                onClick={(event) => {
                  const columnElement = dayRefs.current[idx];
                  if (!columnElement) {
                    return;
                  }
                  const target = event.target as HTMLElement;
                  if (target.closest('[data-consultation-card="true"]')) {
                    return;
                  }

                  const rect = columnElement.getBoundingClientRect();
                  const relativeY = event.clientY - rect.top;
                  const minutesPerPixel = 60 / HEIGHT_HOUR;
                  let minutes = Math.round((relativeY * minutesPerPixel) / 30) * 30;
                  const durationMinutes = 60;
                  const minutesInDay = 24 * 60;
                  minutes = Math.max(0, minutes);
                  const maxStart = minutesInDay - durationMinutes;
                  if (minutes > maxStart) {
                    minutes = maxStart;
                  }

                  const dayDate = dayjs(new Date(el));
                  const start = dayDate.startOf('day').add(minutes, 'minute');
                  const end = start.add(durationMinutes, 'minute');

                  onCreateFromSlot({
                    date: start.startOf('day').toISOString(),
                    start: start.format('HH:mm'),
                    end: end.format('HH:mm'),
                  });
                }}
              >
                {consultationList
                  .filter((item) => getDate(new Date(item.date)) === getDate(new Date(el)))
                  .map((consul) => {
                    const client = clients.find((el) => `/api/v1/clients/${el.id}` === consul.client_entity);
                    const start = getTime(new Date(consul.datetime_start)).split(':');
                    const end = getTime(new Date(consul.datetime_end)).split(':');
                    const difference =
                      Number(end[0]) * 60 + Number(end[1]) - (Number(start[0]) * 60 + Number(start[1]));
                    const clamp = difference / 60;
                    const name =
                      clamp <= 0.5
                        ? `${client?.full_name.slice(0, 9).trim()}...`
                        : clamp <= 1
                          ? `${client?.full_name.slice(0, 16).trim()}...`
                          : clamp <= 1.5
                            ? `${client?.full_name.slice(0, 32).trim()}...`
                            : client?.full_name;

                    return (
                      <div
                        key={consul.id}
                        draggable
                        onDragStart={(event) => {
                          event.stopPropagation();
                          const rect = event.currentTarget.getBoundingClientRect();
                          setDragInfo({
                            consultation: consul,
                            pointerOffset: event.clientY - rect.top,
                          });
                          try {
                            event.dataTransfer.setData('text/plain', String(consul.id));
                          } catch (error) {
                            // ignore for browsers that disallow setData
                          }
                          event.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => {
                          setDragInfo(null);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setHoveredConsultation({
                            consultation: consul,
                            client,
                          });
                        }}
                        data-consultation-card="true"
                        className={classNames(
                          'absolute w-full max-w-[100px] p-[10px_5px] bg-[#DEC05E] text-sm text-blackBase rounded-[5px] flex items-center text-center justify-center cursor-pointer',
                          {
                            'z-[1]': client,
                          },
                        )}
                        style={{
                          top: `${Number(start[0]) * HEIGHT_HOUR + (Number(start[1]) * HEIGHT_HOUR) / 60}px`,
                          height: `${(difference * (HEIGHT_HOUR - 2)) / 60}px`,
                        }}
                        title={client?.full_name}
                      >
                        {name}
                      </div>
                    );
                  })}
              </div>
            ))}
            {hoveredConsultation && (
              <ConsultationHoverPopup
                consultation={hoveredConsultation.consultation}
                client={hoveredConsultation.client}
                onClose={() => setHoveredConsultation(null)}
                onEdit={() => {
                  handleChangeCons(hoveredConsultation.consultation.id, { isEdit: true });
                  setHoveredConsultation(null);
                }}
                onDelete={() => {
                  if (handleOpenDelete) {
                    handleOpenDelete(hoveredConsultation.consultation, true);
                  }
                  setHoveredConsultation(null);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
